import React from "react";

import Button from "@mui/material/Button";
import { OverridableStringUnion } from "@mui/types";
import styled from "styled-components";

import { color } from "../../tokens";
import { ButtonProps } from "../types";

export const ButtonComponent: React.FC<ButtonProps> = (props) => {
  const {
    visible = true,
    dataTestId,
    label,
    children,
    href,
    target,
    ...rest
  } = props;

  if (!visible) {
    return null;
  }

  if (props.variant === "inline-text") {
    return (
      <InlineButton type="button" data-testid={dataTestId} {...rest}>
        {label || children}
      </InlineButton>
    );
  }

  if (props.variant === "link-text") {
    return (
      <LinkButton href={href} target={target}>
        {label || children}
      </LinkButton>
    );
  }

  // MUI Button renders as <a> when href is provided, but its TS types don't
  // include anchor attributes (e.g. target) unless component="a" is declared.
  // We cast to avoid the false type error while keeping runtime behaviour correct.
  const anchorProps = href
    ? ({ href, target } as unknown as React.ComponentProps<typeof Button>)
    : {};

  return (
    <Button
      data-testid={dataTestId}
      {...anchorProps}
      {...rest}
      variant={
        (props.variant as OverridableStringUnion<
          "text" | "outlined" | "contained"
        >) || "contained"
      }
    >
      {label || children}
    </Button>
  );
};

export { ButtonComponent as Button };

const InlineButton = styled.button`
  background: transparent;
  border: none;
  font: inherit;
  font-size: inherit;
  color: ${color.blue[300]};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
`;

const LinkButton = styled.a`
  background: transparent;
  border: none;
  font: inherit;
  font-size: inherit;
  color: ${color.blue[300]};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
`;
